import { Injectable, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Observable, Subject } from "rxjs";
import { io, Socket } from "socket.io-client";
import { environment } from "../../environments/environment";
import { WsData } from "../models";
import { ReferenceSummary } from "../models/reference-summary";
import { AuthInterceptor } from "./auth-interceptor.service";
import { InternalUserControllerService } from "./internal-user-controller.service";

export interface WebSocketInstance {
  socket: Socket;
  message: Subject<string>;
  authValidate: boolean;
}

/**
 * WebSocket service to manage connections and messages using namespaces.
 * Handles authentication, timeout, and message routing.
 */
@Injectable()
export class WebSocketService implements OnDestroy {
  private readonly TIMEOUT: number = 10 * 60 * 1000; // 10 min max between each socket message before timeout

  sockets: Map<string, WebSocketInstance> = new Map();

  private timeout: NodeJS.Timeout;
  private timeoutAuth: NodeJS.Timeout;

  private authInterceptor: AuthInterceptor;

  messages: Subject<any>;

  // Our constructor calls our wsService connect method
  constructor(
    private router: Router,
    private _userService: InternalUserControllerService,
    public dialog: MatDialog
  ) {
    this.authInterceptor = new AuthInterceptor(router, _userService, dialog);
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeout);
    clearTimeout(this.timeoutAuth);
  }

  /**
   * Establishes a WebSocket connection for the given namespace.
   * @param namespace {string} The namespace to connect to (e.g., '/map').
   * @returns {Promise<void>} A promise that resolves when the connection is established.
   */
  public connect(namespace: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const socket: Socket = io(`${environment.apiBaseUrl}${namespace}`, {
          query: { token: this.getToken() },
          path: "/socket.io",
          transports: ["websocket"],
          secure: true
        });

        const message: Subject<string> = new Subject();
        const webSocketInstance: WebSocketInstance = {
          socket: socket,
          message: message,
          authValidate: false
        };

        socket.on("connect", () => {
          this.sockets.set(namespace, webSocketInstance);
          resolve();
        });

        socket.on("error", (err) => {
          this.authInterceptor.redirection();
          reject(err);
        });
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Sends a message through a WebSocket connection.
   * Retries sending until authentication is validated.
   * @param message {WsData} The message object to send.
   * @param namespace {string} The target WebSocket namespace.
   */
  public async sendMessage(message: WsData, namespace: string) {
    let webSocketInstance: WebSocketInstance = this.sockets.get(namespace);

    if (webSocketInstance.authValidate) {
      message.userName = this.getName();
      await webSocketInstance.socket.emit("message", JSON.stringify(message));
    } else {
      this.timeoutAuth = setTimeout(() => {
        this.sendMessage(message, namespace);
      }, 100);
    }
  }

  /**
   * Listens for incoming WebSocket messages from the given namespace.
   * Automatically validates authentication and manages timeout detection.
   * @param namespace {string} The WebSocket namespace.
   * @returns {Observable<string>} Observable stream of raw messages.
   */
  public getNewMessage(namespace: string): Observable<string> {
    let webSocketInstance: WebSocketInstance = this.sockets.get(namespace);
    webSocketInstance.socket.on("message", (message) => {
      if (webSocketInstance.authValidate) {
        webSocketInstance.message.next(message);
        clearTimeout(this.timeout);
        this.timeout = setTimeout(
          () => this.handleTimeout(webSocketInstance.message),
          this.TIMEOUT
        );
      }
      if (!webSocketInstance.authValidate && message === this.getName()) {
        webSocketInstance.authValidate = true;
      }
    });
    return webSocketInstance.message.asObservable();
  }

  /**
   * Disconnects from the specified WebSocket namespace and cleans up.
   * @param namespace {string} The namespace to disconnect from.
   */
  public async disconnect(namespace: string) {
    let webSocketInstance: WebSocketInstance = this.sockets.get(namespace);
    if (webSocketInstance) {
      webSocketInstance.authValidate = false;
      await webSocketInstance.socket.disconnect();
      clearTimeout(this.timeout);
      clearTimeout(this.timeoutAuth);
      this.sockets.delete(namespace);
    }
  }

  /**
   * return the socket for a given namespace (useful to listen to 'connect' and 'disconnect' events).
   */
  public getSocket(namespace: string): Socket | undefined {
    return this.sockets.get(namespace)?.socket;
  }

  /**
   * Returns the current authentication token.
   * @returns {string} The stored token.
   */
  private getToken(): string {
    return localStorage.getItem("password");
  }

  /**
   * Returns the current username.
   * @returns {string} The stored username.
   */
  private getName(): string {
    return localStorage.getItem("username");
  }

  /**
   * Sends a timeout error message when no activity is detected.
   * @param message {Subject<string>} The message subject to emit the timeout error into.
   */
  handleTimeout(message: Subject<string>) {
    const referenceSummary: ReferenceSummary = {
      info: [],
      warnings: ["Timeout error"]
    };
    const wsData: WsData = {
      messageType: "Error",
      userName: this.getName(),
      referenceType: "Timeout",
      referenceSummary: referenceSummary
    };
    message.next(JSON.stringify(wsData));
  }
}
